import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  OutlinedInput, 
  Select, 
  InputLabel,
  Button,
  Container,
  Paper,
  BoxProps,
  CardProps,
  TypographyProps,
  OutlinedInputProps,
  SelectProps,
  InputLabelProps,
  ButtonProps,
  ContainerProps,
  PaperProps
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors, borderRadius, spacing } from '../../theme';

// Background com gradiente
export const GradientBackground = styled(Box)<BoxProps>(({ theme }) => ({
  background: theme.palette.mode === 'light' 
    ? colors.gradient.background
    : colors.gradient.backgroundDark,
  alignItems: 'center',
  display: 'flex',
  flexGrow: 1,
  minHeight: '100vh',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  }
}));

// Container centralizado
export const CenteredContainer = styled(Container)<ContainerProps>(() => ({
  position: 'relative',
  zIndex: 1,
}));

// Card glassmorphism
export const GlassCard = styled(Card)<CardProps>(({ theme }) => ({
  borderRadius: borderRadius.large,
  boxShadow: theme.palette.mode === 'light' 
    ? colors.shadow.glass
    : colors.shadow.glassDark,
  backdropFilter: 'blur(20px)',
  background: theme.palette.mode === 'light'
    ? colors.background.glassmorphism
    : colors.background.glassmorphismDark,
  border: 'none',
  position: 'relative',
  zIndex: 1,
}));

// Card content com padding customizado
export const StyledCardContent = styled(CardContent)(() => ({
  padding: spacing.xl,
}));

// Logo circular
interface LogoCircleProps {
  children: React.ReactNode;
}

export const LogoCircle = styled(Box)<LogoCircleProps>(() => ({
  width: 80,
  height: 80,
  borderRadius: borderRadius.circle,
  background: colors.gradient.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: `0 auto ${spacing.lg}`,
  boxShadow: colors.shadow.button,
  fontSize: '32px',
  color: 'white',
  fontWeight: 'bold',
}));

// Título com gradiente
export const GradientTitle = styled(Typography)<TypographyProps>(() => ({
  fontWeight: 700,
  background: colors.gradient.primary,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textAlign: 'center',
  marginBottom: spacing.sm,
}));

// Subtítulo estilizado
export const StyledSubtitle = styled(Typography)<TypographyProps>(() => ({
  fontWeight: 500,
  color: 'text.secondary',
  textAlign: 'center',
  marginBottom: spacing.lg,
}));

// Label personalizada
export const StyledInputLabel = styled(InputLabel)<InputLabelProps>(() => ({
  marginBottom: spacing.sm,
  fontWeight: 600,
  color: 'text.primary',
}));

// Input estilizado
export const StyledInput = styled(OutlinedInput)<OutlinedInputProps>(() => ({
  borderRadius: borderRadius.medium,
  backgroundColor: colors.background.paper,
  '& .MuiOutlinedInput-root': {
    borderRadius: borderRadius.medium,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary.main,
    },
  },
}));

// Select estilizado
export const StyledSelect = styled(Select)<SelectProps>(() => ({
  borderRadius: borderRadius.medium,
  '& .MuiOutlinedInput-root': {
    borderRadius: borderRadius.medium,
  },
}));

// Botão principal estilizado
export const PrimaryButton = styled(Button)<ButtonProps>(() => ({
  borderRadius: borderRadius.medium,
  textTransform: 'none',
  fontWeight: 600,
  padding: `${spacing.md} ${spacing.xl}`,
  background: colors.gradient.primary,
  boxShadow: colors.shadow.button,
  '&:hover': {
    background: colors.gradient.primaryHover,
    boxShadow: colors.shadow.buttonHover,
  },
}));

// Botão secundário estilizado
export const SecondaryButton = styled(Button)<ButtonProps>(() => ({
  borderRadius: borderRadius.medium,
  textTransform: 'none',
  fontWeight: 600,
  padding: `${spacing.md} ${spacing.lg}`,
}));

// Container de formulário
export const FormContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.md,
}));

// Container de ações
export const ActionsContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.md,
  paddingTop: spacing.md,
  '& > *:first-child': {
    marginRight: 'auto',
  },
}));

// Paper estilizado para páginas internas
export const StyledPaper = styled(Paper)<PaperProps>(({ theme }) => ({
  padding: spacing.xl,
  borderRadius: borderRadius.large,
  boxShadow: theme.palette.mode === 'light' 
    ? colors.shadow.glass
    : colors.shadow.glassDark,
  backdropFilter: 'blur(10px)',
  background: theme.palette.mode === 'light'
    ? colors.background.glassmorphism
    : colors.background.glassmorphismDark,
}));

// Container de página
export const PageContainer = styled(Box)(() => ({
  padding: spacing.lg,
  minHeight: '100vh',
  background: colors.background.default,
}));

// Header de página
export const PageHeader = styled(Box)(() => ({
  marginBottom: spacing.xl,
  textAlign: 'center',
}));

// Wrapper de conteúdo
export const ContentWrapper = styled(Box)(() => ({

  margin: '0 auto',
  width: '100%',
}));